import { INGREDIENTS_SEED, VERSION_SEED } from '../data/ingredients-seed'
import { nowInstant } from '../domain/time'
import type { Ingredient } from '../domain/types'
import { META_SEED_VERSION, db, ecrireMeta, lireMeta } from './db'

/**
 * Installe ou met a jour le referentiel d'ingredients livre.
 *
 * Regle : on n'ecrase **jamais** un ingredient que l'utilisateur a modifie.
 * Une correction apportee sur le niveau FODMAP d'un aliment lui appartient ;
 * une mise a jour de l'app ne doit pas la faire disparaitre. Seuls les
 * nouveaux ingredients sont ajoutes.
 */
export async function installerReferentiel(): Promise<{ ajoutes: number; rafraichis: number }> {
  const versionInstallee = await lireMeta<number>(META_SEED_VERSION, 0)
  if (versionInstallee >= VERSION_SEED) return { ajoutes: 0, rafraichis: 0 }

  const existants = new Map((await db.ingredients.toArray()).map((i) => [i.id, i]))
  const maintenant = nowInstant()

  const nouveaux: Ingredient[] = []
  const rafraichis: Ingredient[] = []

  for (const g of INGREDIENTS_SEED) {
    const actuel = existants.get(g.id)

    if (!actuel) {
      nouveaux.push({
        id: g.id,
        nom: g.nom,
        categorie: g.categorie,
        niveauFodmap: g.niveau,
        famillesFodmap: g.familles,
        synonymes: g.synonymes ?? [],
        perso: false,
        creeLe: maintenant,
        modifieLe: maintenant,
      })
      continue
    }

    // L'entree existe deja. On ne la rafraichit que si elle vient du referentiel
    // et n'a jamais ete touchee par l'utilisateur — ce que trahit un `modifieLe`
    // encore egal a `creeLe`.
    //
    // Sans cela, une base installee avant une correction du referentiel resterait
    // indefiniment sur les anciens libelles : c'est ce qui a laisse « Pates de
    // ble » a cote de « Farine de blé » apres l'accentuation de l'application.
    const intact = !actuel.perso && actuel.creeLe === actuel.modifieLe
    const identique =
      actuel.nom === g.nom &&
      actuel.categorie === g.categorie &&
      actuel.niveauFodmap === g.niveau &&
      actuel.famillesFodmap.join() === g.familles.join()

    if (intact && !identique) {
      rafraichis.push({
        ...actuel,
        nom: g.nom,
        categorie: g.categorie,
        niveauFodmap: g.niveau,
        famillesFodmap: g.familles,
        synonymes: g.synonymes ?? [],
        // `modifieLe` reste egal a `creeLe` : l'entree demeure « intacte » et
        // pourra donc etre rafraichie de nouveau lors d'une future correction.
        modifieLe: actuel.creeLe,
      })
    }
  }

  if (nouveaux.length > 0) await db.ingredients.bulkAdd(nouveaux)
  if (rafraichis.length > 0) await db.ingredients.bulkPut(rafraichis)
  await ecrireMeta(META_SEED_VERSION, VERSION_SEED)

  return { ajoutes: nouveaux.length, rafraichis: rafraichis.length }
}
